import { useEffect, useMemo, useState } from 'react'
import { api, periodenname } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { anzahlwort, belegtext, datenquellenname, gesellschaftsname, satz, t } from './sprache'
import { abbildungLesen } from './Datenquellen'
import { Vorschlagsfrage, alleBeantwortet } from './Bewegungsartvorschlaege'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import { useDarf } from './Rechte'
import type { Aktualisierungsvorschau, Datenquelle, Stapelbericht } from './typen'

/**
 * Alle angemahnten Datenimporte in einem Zug aktualisieren
 * (5.1 Abschnitt 3.39, R932).
 *
 * **Der erste Schritt bucht nichts.** Gelesen werden die aufbewahrten Dateien;
 * was hier steht, ist je Datenimport eine Zeile mit Datei und Befund. Gebucht
 * wird erst auf Knopfdruck — je Gesellschaft und Datenquelle aufsteigend nach
 * Periode, durch denselben Stapel wie viele Dateien auf einmal (R730).
 *
 * **Beträge stehen hier nicht.** Die Verkehrszahl des Juli hängt am Juni, der
 * in demselben Zug erst gebucht wird (R749); eine Zahl davor wäre eine, die so
 * nicht gebucht wird (R145).
 */
export function Sammelaktualisierung({
  aufFertig,
  aufZurueck,
}: {
  /** Gebucht — mit dem Satz für das Kästchen (R1130) und den Läufen, die die Liste markiert (R1136). */
  aufFertig: (erfolgssatz: string, neu: { laeufe: number[] }) => void
  aufZurueck: () => void
}) {
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('datenimport:sammelaktualisierung')

  const darf = useDarf()
  // Ohne das Recht wird nicht gelesen; der Weg hierher fehlt dann schon in der Liste (R1180).
  const vorschauErlaubt = darf('aktualisierungsvorschau')
  const [vorschau, setVorschau] = useState<Aktualisierungsvorschau | null>(null)
  const [quellen, setQuellen] = useState<Datenquelle[]>([])
  const [fehler, setFehler] = useState(false)
  /**
   * Was dazu am Ort steht — nur ein fachlicher Satz; eine Störung meldet das
   * Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
   */
  const [fehlersatz, setFehlersatz] = useState<string | null>(null)
  const [bucht, setBucht] = useState(false)
  const [bericht, setBericht] = useState<Stapelbericht | null>(null)
  /** Die Antwort je Vorschlag, erkannt an Datenquelle, Konto und Richtung (R871, R873). */
  const [antworten, setAntworten] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (!vorschauErlaubt) return
    let gilt = true
    Promise.all([api.aktualisierungsvorschau(), api.datenquellen()])
      .then(([v, q]) => {
        if (gilt) {
          setVorschau(v)
          setQuellen(q)
        }
      })
      .catch((f) => {
        if (gilt) {
          setFehler(true)
          setFehlersatz(fachlicherSatz(f))
        }
      })
    return () => {
      gilt = false
    }
  }, [vorschauErlaubt])

  const zuBuchen = useMemo(() => (vorschau?.eintraege ?? []).filter((e) => !e.bleibt), [vorschau])

  /*
    **Gefragt wird jeder Vorschlag der beteiligten Datenquellen, einmal für den
    Zug** (R873) — nicht nur der getroffene: Welcher Monat getroffen ist, steht
    erst beim Buchen fest (R749).
  */
  const fragen = useMemo(
    () =>
      [...new Set(zuBuchen.map((e) => e.datenquelle).filter((q): q is string => q !== null))].flatMap((schluessel) =>
        abbildungLesen(quellen.find((q) => q.schluessel === schluessel)?.abbildungJson ?? null).vorschlaege.map(
          (v) => ({
            ...v,
            quelle: schluessel,
            zeilen: 0,
            betrag: 0,
            angenommen: antworten[`${schluessel}|${v.konto}|${v.richtung}`] ?? null,
          }),
        ),
      ),
    [zuBuchen, quellen, antworten],
  )
  const beantwortet = alleBeantwortet(fragen)

  async function uebernehmen() {
    setBucht(true)
    try {
      const ergebnis = await api.aktualisierungUebernehmen(
        [...new Set(fragen.map((f) => f.quelle))].map((quelle) => ({
          quelle,
          antworten: fragen
            .filter((f) => f.quelle === quelle)
            .map((f) => ({ konto: f.konto, richtung: f.richtung, angenommen: f.angenommen === true })),
        })),
      )

      if (ergebnis.istVollstaendig) {
        // Ohne die Zahl (7.2 Abschnitt 34, R1131); die Liste zeigt sie (R1136).
        aufFertig(t('erfolg.datenimporte.aktualisiert'), { laeufe: ergebnis.gebucht.map((g) => g.lauf) })
        return
      }

      setBericht(ergebnis)
    } catch (f) {
      setFehler(true)
      setFehlersatz(fachlicherSatz(f))
    } finally {
      setBucht(false)
    }
  }

  return (
    <section>
      <div className="feldzeile">
        <h1>{t('import.aktualisierung.titel')}</h1>
        <button type="button" onClick={aufZurueck}>
          {t('import.stand.zurueck')}
        </button>
      </div>

      <p className="erlaeuterung">{t('import.aktualisierung.hinweis')}</p>

      {fehlersatz && <p className="hinweis">{fehlersatz}</p>}
      {vorschauErlaubt && !vorschau && !fehler && <p className="erlaeuterung">{t('import.aktualisierung.liest')}</p>}

      {vorschau && (
        <>
          {/* Einmal für den Zug, nicht je Monat (R873). */}
          <Vorschlagsfrage
            vorschlaege={fragen}
            mitUmfang={false}
            laeuft={bucht}
            aufAntwort={(i, angenommen) =>
              setAntworten((a) => ({
                ...a,
                [`${fragen[i]!.quelle}|${fragen[i]!.konto}|${fragen[i]!.richtung}`]: angenommen,
              }))
            }
          />

          {/* Wer nicht buchen darf, sieht den Knopf nicht und auch nicht, warum er gesperrt wäre (R1180). */}
          {darf('aktualisierungUebernehmen') && (
            <p className="handreichung">
              <button
                type="button"
                className="haupt"
                disabled={bucht || !vorschau.istBuchbar || !beantwortet || bericht !== null}
                onClick={() => void uebernehmen()}
              >
                {t(bucht ? 'import.aktualisierung.buchtgerade' : 'import.aktualisierung.uebernehmen', {
                  anzahl: anzahlwort(zuBuchen.length),
                })}
              </button>

              {/* Nur der gesperrte Knopf sagt warum (R144). */}
              {!vorschau.istBuchbar && <span className="erlaeuterung"> {t('import.aktualisierung.gesperrt')}</span>}
              {vorschau.istBuchbar && !beantwortet && (
                <span className="erlaeuterung"> {t('import.uebernehmen.vorschlaege')}</span>
              )}
            </p>
          )}

          {bericht && (
            <div className="hinweis">
              <p>{t('import.aktualisierung.halt', { gebucht: anzahlwort(bericht.gebucht.length) })}</p>
              {bericht.halt && <p>{satz(bericht.halt)}</p>}
            </div>
          )}

          <div className="tabellenrahmen">
            <table ref={spaltenrahmen} className="ziehbaretabelle">
              <Spaltenmassleiste masse={spaltenmasse} />
              <thead>
                <tr>
                  <th>{t('import.spalte.einheit')}</th>
                  <th>{t('import.spalte.periode')}</th>
                  <th>{t('import.spalte.belegname')}</th>
                  <th>{t('import.schritt.quelle')}</th>
                  <th>{t('import.stapel.spalte.datei')}</th>
                  <th>{t('import.spalte.befund')}</th>
                </tr>
              </thead>
              <tbody>
                {vorschau.eintraege.map((e) => (
                  <tr key={e.belegId} className={e.istBuchbar ? undefined : 'geaendert'}>
                    <td>{gesellschaftsname(e.einheit)}</td>
                    <td>{periodenname(e.periode)}</td>
                    <td>{belegtext(e.name)}</td>
                    <td>{e.datenquelle ? datenquellenname(e.datenquelle) : '—'}</td>
                    <td className="code">{e.dateiname ?? '—'}</td>
                    <td>
                      {bericht?.gebucht.some((g) => g.name === e.name && g.periode === e.periode)
                        ? t('import.stapel.stand.gebucht')
                        : e.befund
                          ? satz(e.befund)
                          : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  )
}
